import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  Button,
  PageHeading,
  Icon,
  ConfirmDialog,
  useAuth,
  employeeID,   
  companyID, 
} from "../../../Common/UIComponents";
import {
  fetchFacilities,
  deleteTwoColumnListEntry,
  updateFacilitySortOrder,
} from "../../../API_Service/Settings_Slice";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { URL } from "../../../Common/APIURL";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id:          item.Facility_Id        ?? `row-${i}`,
    name:        item.Facility_Name      ?? "Unknown",
    tempMin:     item.TemperatureMin     ?? null,
    tempMax:     item.TemperatureMax     ?? null,
    humidityMin: item.HumidityMin        ?? null,
    humidityMax: item.HumidityMax        ?? null,
    status:      item.Status             ?? "AC",
    description: item.Description        ?? "",
    sortOrder:   item.SortOrder          ?? 0,
    imagePath:   item.FacilityImagePath  ?? "",
  }));

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const EXPORT_COLUMNS = [
  { key: "name",        title: "Lab Name"         },
  { key: "tempMin",     title: "Temp Min (°C)"    },
  { key: "tempMax",     title: "Temp Max (°C)"    },
  { key: "humidityMin", title: "Humidity Min (%)" },
  { key: "humidityMax", title: "Humidity Max (%)" },
  // { key: "status",      title: "Status"           },
];

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────

const Tooltip = ({ children, content, maxW = "max-w-xs" }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0, flip: false });
  const triggerRef            = useRef(null);

  const show = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const flip = rect.top > window.innerHeight / 2;
    setPos({ top: flip ? rect.top - 8 : rect.bottom + 8, left: rect.left + rect.width / 2, flip });
    setVisible(true);
  };

  return (
    <span ref={triggerRef} className="relative inline-block" onMouseEnter={show} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div
          className={`fixed z-[9999] pointer-events-none ${maxW} w-max bg-[#181c1d] text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed -translate-x-1/2 ${pos.flip ? "-translate-y-full" : ""}`}
          style={{ top: pos.top, left: pos.left }}
        >
          {content}
          {pos.flip
            ? <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#181c1d]" />
            : <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-[#181c1d]" />
          }
        </div>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total = rows.length;

  const avgTemp = useMemo(() => {
    const valid = rows.filter((r) => r.tempMin != null && r.tempMax != null);
    if (!valid.length) return null;
    const avg =
      valid.reduce((sum, r) => sum + (r.tempMin + r.tempMax) / 2, 0) /
      valid.length;
    return avg.toFixed(1);
  }, [rows]);

  const avgHumidity = useMemo(() => {
    const valid = rows.filter(
      (r) => r.humidityMin != null && r.humidityMax != null
    );
    if (!valid.length) return null;
    const avg =
      valid.reduce((sum, r) => sum + (r.humidityMin + r.humidityMax) / 2, 0) /
      valid.length;
    return avg.toFixed(1);
  }, [rows]);

  const stats = [
    {
      label:     "Total Facilities",
      value:     total,
      suffix:    "",
      icon:      "table_rows",
      iconBg:    "bg-[#005b60]/10",
      iconColor: "text-[#005b60]",
      sparkline: true,
    },
    {
      label:     "Avg Temperature",
      value:     avgTemp ?? "—",
      suffix:    avgTemp ? "°C" : "",
      icon:      "device_thermostat",
      iconBg:    "bg-[#006fa8]/10",
      iconColor: "text-[#006fa8]",
      sparkline: false,
    },
    {
      label:     "Avg Humidity",
      value:     avgHumidity ?? "—",
      suffix:    avgHumidity ? "%" : "",
      icon:      "water_drop",
      iconBg:    "bg-[#436900]/10",
      iconColor: "text-[#436900]",
      sparkline: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white p-3.5 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50"
        >
          <p className="text-[#3e494a] text-xs font-bold uppercase tracking-widest mb-1.5">
            {s.label}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
                <span className="material-symbols-outlined text-[20px]">{s.icon}</span>
              </div>
              <h3 className="text-2xl font-black text-[#181c1d]">
                {s.value}{s.suffix}
              </h3>
            </div>
            {s.sparkline && (
              <div className="w-24 h-10 bg-[#006fa8]/5 rounded overflow-hidden">
                <div
                  className="h-full w-full opacity-40 bg-gradient-to-r from-[#006fa8] to-transparent"
                  style={{
                    clipPath:
                      "polygon(0 100%, 10% 80%, 25% 90%, 40% 60%, 55% 75%, 70% 40%, 85% 55%, 100% 20%, 100% 100%)",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

const RangeCell = ({ min, max, unit }) => {
  if (min == null && max == null) return <span className="text-xs text-[#3e494a]">—</span>;
  return (
    <div className="inline-flex items-center gap-1 bg-[#e5e9e9] px-2 py-1 rounded-lg">
      <span className="text-xs font-mono font-bold text-[#181c1d]">
        {min ?? "—"}{unit} – {max ?? "—"}{unit}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function FacilityConfigIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [facilities,    setFacilities]    = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError,   setDeleteError]   = useState(null);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [deleteDialog,  setDeleteDialog]  = useState({ open: false, row: null });
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [editSuccess,   setEditSuccess]   = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [facilityRows,  setFacilityRows]  = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const fetchList = useCallback(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(fetchFacilities({ ID: 0, companyID: companyID }))
      .then((res) => setFacilities(toArray(res)))
      .catch((err) => setSettingsError(err?.message ?? "Failed to load facilities"))
      .finally(() => setIsLoading(false));
  }, [dispatch]); 

  useEffect(() => {
    fetchList();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (location.state?.refresh) {
    fetchList();
    if (location.state?.editSuccess) {
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }
}, [location.state?.refresh]);

  const rows = useMemo(
    () => buildTableRows(facilities).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [facilities]
  );

  // Keep drag-and-drop rows in sync whenever the fetched list changes
  useEffect(() => { setFacilityRows(rows); }, [rows]);

  const filteredFacilityRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return facilityRows;
    return facilityRows.filter((r) => {
      // Build formatted range strings for search
      const tempRange = r.tempMin != null && r.tempMax != null
        ? `${r.tempMin}°C – ${r.tempMax}°C`.toLowerCase()
        : "";
      const humidityRange = r.humidityMin != null && r.humidityMax != null
        ? `${r.humidityMin}% – ${r.humidityMax}%`.toLowerCase()
        : "";

      return (
        r.name.toLowerCase().includes(q) ||
        String(r.tempMin ?? "").includes(q) ||
        String(r.tempMax ?? "").includes(q) ||
        String(r.humidityMin ?? "").includes(q) ||
        String(r.humidityMax ?? "").includes(q) ||
        tempRange.includes(q) ||
        humidityRange.includes(q)
      );
    });
  }, [facilityRows, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredFacilityRows.length / pageSize));

  const paginatedFacilityRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredFacilityRows.slice(start, start + pageSize);
  }, [filteredFacilityRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  const handleEdit = useCallback(
    (row) => navigate("/application-config/facilities/create", { state: { mode: "edit", data: row } }),
    [navigate]
  );

  const handleDelete       = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete = useCallback(() => { setDeleteDialog({ open: false, row: null }); setDeleteBlocked(false); }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(deleteTwoColumnListEntry({ term: "FACILITY", id: row.id, modifiedBy: employeeID, companyID }))
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        setDeleteBlocked(false);
        setDeleteDialog({ open: false, row: null });
        setDeleteSuccess(true);
        fetchList();
        setTimeout(() => setDeleteSuccess(false), 3000);
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch, companyID, employeeID, fetchList]);

  const handleAddNew = useCallback(
    () => navigate("/application-config/facilities/create"),
    [navigate]
  );

  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  const saveFacilityOrder = useCallback((orderedRows) => {
    setIsSavingOrder(true);
    dispatch(updateFacilitySortOrder({
      CompanyID: companyID,
      Items: orderedRows.map((row, i) => ({ facilityID: row.id, sortOrder: i + 1 })),
    })).finally(() => setIsSavingOrder(false));
  }, [dispatch]);

  const handleSortEnd = useCallback((reorderedPage) => {
    // reorderedPage is the current page's rows in their new order.
    // Reconstruct the full list: find each page item's position in facilityRows
    // by ID and swap them into their new positions.
    const newRows = [...facilityRows];
    const pageItemIds  = paginatedFacilityRows.map((r) => r.id);
    const fullIndices  = pageItemIds.map((id) => newRows.findIndex((r) => r.id === id));
    reorderedPage.forEach((row, i) => { newRows[fullIndices[i]] = row; });
    setFacilityRows(newRows);
    saveFacilityOrder(newRows);
  }, [facilityRows, paginatedFacilityRows, saveFacilityOrder]);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(() => [
    {
      key:   "name",
      title: "Lab Name",
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.imagePath ? (
            <img
              src={`${URL}${row.imagePath}`}
              alt={row.name}
              className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="w-8 h-8 rounded-lg bg-[#005b60]/10 items-center justify-center text-[#005b60] flex-shrink-0"
            style={{ display: row.imagePath ? "none" : "flex" }}
          >
            <span className="material-symbols-outlined text-[18px]">cell_tower</span>
          </div>
          <span className="font-bold text-[#181c1d]">{row.name}</span>
        </div>
      ),
    },
    {
      key:   "tempRange",
      title: "Temperature Range",
      render: (row) => <RangeCell min={row.tempMin} max={row.tempMax} unit="°C" />,
    },
    {
      key:   "humidityRange",
      title: "Humidity Range",
      render: (row) => <RangeCell min={row.humidityMin} max={row.humidityMax} unit="%" />,
    },
  ], []);

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">



        {/* Page Header */}
        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <PageHeading
            title="Facility Management"
            subtitle="Manage laboratory facilities, environment parameters, and resource allocation across your clinical operations."
          />
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add New Facility
          </Button>
        </div>

        {/* Stats */}
        <StatsRow rows={rows} />

        {/* Table */}
        <ConfigTable
          persistKey="application-config-facility"
          columns={columns}
          rows={isLoading ? [] : paginatedFacilityRows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredFacilityRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="facilities"
          emptyText={isLoading ? "Loading…" : "No facilities found."}
          sortable={true}
          onSortEnd={handleSortEnd}
          isSavingOrder={isSavingOrder}
          showPagination={true}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
        )}
      </div>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={deleteBlocked ? "This facility is already mapped with another module. Please unmap it before deleting." : null}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Delete Success Toast */}
      {deleteSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Facility Deleted successfully.
        </div>
      )}
      {editSuccess && (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
    <Icon name="check_circle" className="text-[20px]" filled />
    Facility Updated successfully.
  </div>
)}

      {/* Delete Error Toast */}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}

